import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import API from "../api";
import { Card, Input, Button, Toast, SectionHeader } from "../components/UI";

const TWO_WHEELER = ["Hero","Honda","TVS","Bajaj","Yamaha","Suzuki","Royal Enfield","KTM","Ola Electric","Ather Energy","Revolt"];
const FOUR_WHEELER = ["Maruti Suzuki","Hyundai","Tata Motors","Mahindra","Kia","Toyota","MG Motor","Skoda","Volkswagen","Ford","Renault","Nissan"];

const INIT = {
  name: "", brand: "", city: "", botType: "sales",
  whatsappPhoneId: "", whatsappToken: "",
  googleSheetUrl: "",
  botProfile_address: "", botProfile_phone: "",
  botProfile_hours: "", botProfile_offers: "",
  botProfile_extraInfo: ""
};

export default function AddClient() {
  const [form, setForm] = useState(INIT);
  const [loading, setLoading] = useState(false);
  const [toast, setToast] = useState(null);
  const [newClientId, setNewClientId] = useState(null);
  const [connectLoading, setConnectLoading] = useState(false);
  const navigate = useNavigate();

  const handleChange = e => setForm(p => ({ ...p, [e.target.name]: e.target.value }));
  const personal = form.brand === "Personal";

  const handleSubmit = async e => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await API.post("/client", {
        ...form,
        botProfile: {
          address: form.botProfile_address,
          phone: form.botProfile_phone,
          hours: form.botProfile_hours,
          offers: form.botProfile_offers,
          extraInfo: form.botProfile_extraInfo,
        }
      });
      const clientId = res.data._id || res.data.id;
      setNewClientId(clientId);
      setToast({ message: "Client added! Ab WhatsApp connect karo.", type: "success" });
    } catch (err) {
      setToast({ message: err.response?.data?.error || "Failed", type: "error" });
    } finally {
      setLoading(false);
    }
  };

  const handleWhatsAppConnect = async () => {
    if (!newClientId) return;
    setConnectLoading(true);
    try {
      const res = await API.get(`/auth/onboard-url/${newClientId}`);
      window.location.href = res.data.onboardUrl;
    } catch (err) {
      setToast({ message: "WhatsApp connect failed. Edit client se try karo.", type: "error" });
      setConnectLoading(false);
    }
  };

  return (
    <div className="fade-in" style={{ maxWidth: 560 }}>
      <SectionHeader title="Add New Client" subtitle="Register a dealership or personal AI bot" />
      <Card>
        {newClientId ? (
          // ── Step 2: WhatsApp Connect ──
          <div style={{ display: "flex", flexDirection: "column", gap: 16, textAlign: "center", padding: "20px 0" }}>
            <div style={{ fontSize: 40 }}>🎉</div>
            <div style={{ fontSize: 18, fontWeight: 700 }}>Client Added!</div>
            <div style={{ fontSize: 14, color: "var(--text-secondary)", lineHeight: 1.7 }}>
              Ab WhatsApp Business account connect karo — ek click mein ho jayega.
            </div>
            <button
              onClick={handleWhatsAppConnect}
              disabled={connectLoading}
              style={s.connectBtn}
            >
              {connectLoading
                ? <span style={{ display: "flex", alignItems: "center", gap: 8, justifyContent: "center" }}>
                    <span style={s.spinner} /> Redirecting...
                  </span>
                : "🔗 Connect WhatsApp Business"}
            </button>
            <button
              onClick={() => navigate("/app/clients")}
              style={s.skipBtn}
            >
              Baad mein connect karunga →
            </button>
          </div>
        ) : (
          // ── Step 1: Client Form ──
          <form onSubmit={handleSubmit} style={{ display: "flex", flexDirection: "column", gap: 16 }}>

            <div style={s.sectionLabel}>Basic Info</div>

            <Input label="Client Name" name="name" value={form.name} onChange={handleChange}
              placeholder={personal ? "e.g. Harish Chandra" : "e.g. Mehrotra Hero Agra"} required />

            <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
              <label style={s.label}>Brand <span style={{ color: "var(--accent-red)" }}>*</span></label>
              <select name="brand" value={form.brand} onChange={handleChange} required style={s.select}>
                <option value="">-- Select --</option>
                <optgroup label="── Personal Bot">
                  <option value="Personal">🤖 Personal (AI Sales Bot)</option>
                </optgroup>
                <optgroup label="── 2-Wheeler Brands">
                  {TWO_WHEELER.map(b => <option key={b} value={b}>{b}</option>)}
                </optgroup>
                <optgroup label="── 4-Wheeler Brands">
                  {FOUR_WHEELER.map(b => <option key={b} value={b}>{b}</option>)}
                </optgroup>
                <optgroup label="── Other">
                  <option value="Other">Other</option>
                </optgroup>
              </select>
            </div>

            {form.brand && form.brand !== "Personal" && (
              <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                <label style={s.label}>Bot Type</label>
                <select name="botType" value={form.botType} onChange={handleChange} style={s.select}>
                  <option value="sales">🏍️ Sales Bot</option>
                  <option value="service">🔧 Service Bot</option>
                </select>
              </div>
            )}

            <Input label="City" name="city" value={form.city} onChange={handleChange} placeholder="e.g. Ramnagar" />

            <div style={{ height: 1, background: "var(--border)" }} />
            <div style={s.sectionLabel}>Google Sheet (Optional)</div>
            <Input label="Webhook URL" name="googleSheetUrl" value={form.googleSheetUrl}
              onChange={handleChange} placeholder="Apps Script Web App URL" />

            <div style={{ height: 1, background: "var(--border)" }} />
            <div style={s.sectionLabel}>Bot Profile</div>
            <Input label="Showroom Address" name="botProfile_address" value={form.botProfile_address}
              onChange={handleChange} placeholder="e.g. Pampapuri Road, Ramnagar" />
            <Input label="Contact Number" name="botProfile_phone" value={form.botProfile_phone}
              onChange={handleChange} placeholder="e.g. 9876543210" />
            <Input label="Business Hours" name="botProfile_hours" value={form.botProfile_hours}
              onChange={handleChange} placeholder="e.g. Mon-Sat 9AM-6PM" />
            <Input label="Current Offers" name="botProfile_offers" value={form.botProfile_offers}
              onChange={handleChange} placeholder="e.g. 0% EMI on Splendor this month" />
            <Input label="Extra Info" name="botProfile_extraInfo" value={form.botProfile_extraInfo}
              onChange={handleChange} placeholder="Jo bhi bot ko pata hona chahiye" />

            <div style={{ display: "flex", gap: 10, justifyContent: "flex-end", marginTop: 8 }}>
              <Button variant="secondary" onClick={() => navigate("/app/clients")} type="button">Cancel</Button>
              <Button type="submit" disabled={loading || !form.name || !form.brand}>
                {loading ? "Saving..." : "⊕ Add Client"}
              </Button>
            </div>

          </form>
        )}
      </Card>
      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}
    </div>
  );
}

const s = {
  sectionLabel: {
    fontSize: 11, fontWeight: 700, color: "var(--text-muted)",
    textTransform: "uppercase", letterSpacing: "0.08em", fontFamily: "var(--font-mono)"
  },
  label: {
    fontSize: 12, color: "var(--text-secondary)", fontWeight: 500,
    textTransform: "uppercase", letterSpacing: "0.05em"
  },
  select: {
    background: "var(--bg-elevated)", border: "1px solid var(--border)",
    borderRadius: "var(--radius)", padding: "10px 14px",
    color: "var(--text-primary)", fontSize: 14,
    fontFamily: "var(--font-body)", outline: "none", width: "100%", cursor: "pointer"
  },
  connectBtn: {
    width: "100%", padding: "14px 20px",
    borderRadius: "var(--radius)",
    background: "#25d366",
    color: "#000", border: "none",
    cursor: "pointer", fontSize: 15,
    fontWeight: 700, fontFamily: "var(--font-body)",
    boxShadow: "0 0 24px rgba(37,211,102,0.2)",
  },
  skipBtn: {
    background: "transparent",
    border: "none",
    color: "var(--text-muted)",
    fontSize: 13,
    cursor: "pointer",
    fontFamily: "var(--font-body)",
    padding: "8px",
  },
  spinner: {
    width: 14, height: 14,
    border: "2px solid rgba(0,0,0,0.2)",
    borderTop: "2px solid #000",
    borderRadius: "50%",
    display: "inline-block",
    animation: "spin 0.7s linear infinite",
  },
};